/**
 * Search adapter
 * Converts search results to Analyzify format
 */

import type { StorefrontProduct } from '../types/storefront';
import type { AnalyzifySearch } from '../types/products';
import { adaptProduct } from './product';

/**
 * Adapt search results to Analyzify format
 * Field names verified against CDN scripts:
 * - term: used by an_fb.js, an_klaviyo.js, an_tiktok.js, an_x.js
 * - products: used by an_bing.js
 * - resultsCount: used by an_klaviyo.js
 * Defensively handles null/undefined inputs
 */
export function adaptSearch(
  term: string | null | undefined,
  products: StorefrontProduct[] | null | undefined,
  totalCount: number | null | undefined
): AnalyzifySearch {
  // Safely process products array
  const safeProducts = Array.isArray(products) ? products : [];
  const safeTerm = term ?? null;
  const safeCount = typeof totalCount === 'number' && !isNaN(totalCount) ? totalCount : 0;

  return {
    term: safeTerm,
    products: safeProducts.map((product) => adaptProduct(product).product),
    resultsCount: safeCount,
    searchPerformed: safeTerm !== null && safeTerm.length > 0,
  };
}

/**
 * Create an empty search result object
 */
export function createEmptySearch(): AnalyzifySearch {
  return {
    term: null,
    products: [],
    resultsCount: 0,
    searchPerformed: false,
  };
}

/**
 * Get search data object for window.analyzify.getSearchObj
 * Defensively handles null/undefined inputs
 */
export function getSearchDataObject(
  term: string | null | undefined,
  products: StorefrontProduct[] | null | undefined,
  totalCount: number | null | undefined
): AnalyzifySearch {
  return adaptSearch(term, products, totalCount);
}
